import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@vehix/portal/components/ui/dialog';
import { Button } from '@vehix/portal/components/ui/button';
import { Label } from '@vehix/portal/components/ui/label';
import { AlertTriangle, Save } from 'lucide-react';

const WARN_BOX_CLASS = 'space-y-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2';
import { EmailChipsInput } from './EmailChipsInput';
import { sameRecipients, schedulesOverriding } from '../../utils/scheduleRecipients';
import type { PipelineSchedule } from '../../services/pipelineOrchestrator';

interface ScheduleSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedules: PipelineSchedule[];
  /** Derived from the schedules — see scheduleRecipients. */
  shared: string[];
  onSave: (schedules: PipelineSchedule[], emails: string[]) => Promise<void>;
}

/**
 * Notification recipients as one setting for every schedule.
 *
 * The orchestrator has no settings endpoint, so "shared" means every schedule
 * holds the same copy. Saving therefore writes to each one, and the dialog says
 * so plainly rather than implying a single stored value — the count in the
 * button is the honest version of what is about to happen.
 *
 * Schedules deliberately using different recipients are listed and left alone;
 * an explicit opt-in is required to overwrite them.
 */
// eslint-disable-next-line max-lines-per-function
export const ScheduleSettingsDialog: React.FC<ScheduleSettingsDialogProps> = ({
  open,
  onOpenChange,
  schedules,
  shared,
  onSave
}) => {
  const [emails, setEmails] = useState<string[]>(shared);
  const [includeOverrides, setIncludeOverrides] = useState(false);
  const [saving, setSaving] = useState(false);

  // Re-seed each time it opens. Depending on `shared` alone would reset the
  // field mid-edit whenever the schedules query refetched.
  useEffect(() => {
    if (open) {
      setEmails(shared);
      setIncludeOverrides(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const overriding = schedulesOverriding(schedules, shared);
  const following = schedules.filter(s => !overriding.includes(s));
  const targets = includeOverrides ? schedules : following;
  const changed = !sameRecipients(emails, shared);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(targets, emails);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-lg'>
        <DialogHeader className='space-y-1'>
          <DialogTitle>Schedule settings</DialogTitle>
          <DialogDescription>Who gets notified when pipeline schedules run.</DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-1'>
          <div className='space-y-1.5'>
            <Label className='text-xs'>Notification recipients</Label>
            <EmailChipsInput value={emails} onChange={setEmails} />
          </div>

          {overriding.length > 0 && (
            <div className={WARN_BOX_CLASS}>
              <p className='flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400'>
                <AlertTriangle className='mt-px h-3.5 w-3.5 shrink-0' />
                <span>
                  {overriding.length} schedule{overriding.length === 1 ? ' uses' : 's use'}{' '}
                  different recipients and will be left alone.
                </span>
              </p>
              <ul className='ml-5 space-y-0.5'>
                {overriding.slice(0, 4).map(s => (
                  <li key={s.id} className='text-[11px] text-muted-foreground'>
                    {s.schedule_name} — {(s.notification_emails ?? []).join(', ') || 'none'}
                  </li>
                ))}
                {overriding.length > 4 && (
                  <li className='text-[11px] text-muted-foreground'>
                    …and {overriding.length - 4} more
                  </li>
                )}
              </ul>
              <label className='ml-5 flex cursor-pointer items-center gap-1.5 text-[11px]'>
                <input
                  type='checkbox'
                  checked={includeOverrides}
                  onChange={e => setIncludeOverrides(e.target.checked)}
                />
                Overwrite these too
              </label>
            </div>
          )}

          <p className='text-xs text-muted-foreground'>
            Recipients are stored on each schedule, so saving updates{' '}
            <span className='font-medium'>
              {targets.length} schedule{targets.length === 1 ? '' : 's'}
            </span>
            . A schedule can still be given its own recipients from its edit window.
          </p>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !changed || targets.length === 0}>
            <Save className='mr-1 h-4 w-4' />
            {changed
              ? `Save to ${targets.length} schedule${targets.length === 1 ? '' : 's'}`
              : 'No changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
